import { Flex, FlexProps, Image, Skeleton, Text } from '@chakra-ui/react';
import { UserType } from 'modules/User';

import { Thumbnail } from './Elements/Elements.types';
import {
  THUMBNAIL_BUTTON_HEIGHT,
  THUMBNAIL_IMAGE_HEIGHT,
  THUMBNAIL_IMAGE_HEIGHT_WITH_CAPTION,
  THUMBNAIL_IMAGE_WIDTH,
  THUMBNAIL_IMAGE_WIDTH_WITH_CAPTION,
} from './ElementsMenu.config';

interface ElementThumbnailProps {
  id: string;
  index: number;
  onClick: (index: number) => void;
  thumbnail: Thumbnail;
  onClickUpgrade?: (upgradePlan?: UserType) => void;
  upgradePlan?: UserType;
  isAccessAllowed?: boolean;
  caption?: string;
  permission?: UserType;
  buttonContainerProps?: FlexProps;
}

const elementButtonFocus = {
  backgroundColor: 'gray.100',
  outline: '2px solid',
  outlineColor: 'upgrade.500',
};

const elementButtonHover = {
  backgroundColor: 'hover.gray',
};

export const ElementThumbnail = ({
  id,
  index,
  onClick,
  onClickUpgrade,
  isAccessAllowed,
  thumbnail,
  caption,
  upgradePlan,
  buttonContainerProps,
}: ElementThumbnailProps) => {
  const { src } = thumbnail;
  const handleClick = () => {
    if (!isAccessAllowed && typeof onClickUpgrade === 'function') {
      return onClickUpgrade(upgradePlan);
    }
    onClick?.(index);
  };

  return (
    <Flex
      _hover={elementButtonHover}
      _focus={elementButtonFocus}
      role='group'
      w={'full'}
      h={'full'}
      maxH={`${THUMBNAIL_BUTTON_HEIGHT}px`}
      direction={'column'}
      gap={1}
      p={4}
      position='relative'
      aria-label={`elements-menu-${id}-btn`}
      alignItems={'center'}
      justifyContent={'center'}
      as={'button'}
      border={'1px solid'}
      borderColor={'divider.gray'}
      borderRadius={'base'}
      onClick={handleClick}
      transitionDuration={'200ms'}
      data-testid={`elements-menu-${id}-btn`}
      {...buttonContainerProps}
    >
      <Image
        src={src}
        objectFit={'contain'}
        h='100%'
        w='auto'
        maxH={caption ? `${THUMBNAIL_IMAGE_HEIGHT_WITH_CAPTION}px` : 'auto'}
        alt=''
        role='presentation'
        pointerEvents='none'
        // use skeleton loader since images are loaded from cdn
        fallback={
          <Skeleton
            color='white'
            w={caption ? `${THUMBNAIL_IMAGE_WIDTH_WITH_CAPTION}px` : `${THUMBNAIL_IMAGE_WIDTH}px`}
            h={caption ? `${THUMBNAIL_IMAGE_HEIGHT_WITH_CAPTION}px` : `${THUMBNAIL_IMAGE_HEIGHT}px`}
          />
        }
      />
      {caption && (
        <Text align='center' fontSize='xs'>
          {caption}
        </Text>
      )}
    </Flex>
  );
};

ElementThumbnail.displayName = 'ElementThumbnail';
